import React, { useEffect, useState, useReducer } from 'react'
import { Link } from 'react-router-dom'
import useSocket from 'use-socket.io-client'
import useSWR from 'swr'

import storage from './storage'
import { handleResponse } from './api'

function useOrder(values = ['↑', '↓'], initial = '↓') {
  const [order, setOrder] = useState(initial)

  function switchOrder() {
    setOrder((order) => {
      const currentIndex = values.indexOf(order)
      const isLast = currentIndex === values.length - 1
      return values[isLast ? 0 : currentIndex + 1]
    })
  }

  return [order, switchOrder]
}

function addValuesToRequest(user, req) {
  return {
    userId: user._id,
    userName: user.name,
    ...req,
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET':
      return {
        ...state,
        users: action.users,
        quote: action.quote,
        supervision: action.supervision,
      }
    default:
      throw new Error()
  }
}

export default function Home() {
  const { data: profile } = useSWR('pxmusic_admin', storage)
  const { data: users, error, mutate } = useSWR(
    profile && profile.token ? '/admin/users' : null,
    (url) =>
      fetch(url, {
        headers: {
          Authorization: `Bearer ${profile.token}`,
          'Content-Type': 'application/json',
        },
      }).then(handleResponse),
    {
      suspense: true,
    }
  )

  const [state, dispatch] = useReducer(reducer, {
    users: [],
    quote: [],
    supervision: [],
  })

  const [quotesOrder, changeQuotesOrder] = useOrder()
  const [supervisionOrder, changeSupervisionOrder] = useOrder()

  const [socket] = useSocket(`/`, {
    autoConnect: false,
  })

  useEffect(() => {
    socket.connect()
    socket.on('connect', function () {
      socket.emit('room', 'admin')
    })
    socket.on('new-entry', () => mutate())
    socket.on('message-read', () => mutate())
    return () => socket.disconnect()
  }, [socket, mutate])

  useEffect(() => {
    if (users) {
      const { quote, supervision } = users.reduce(
        (acc, user) => {
          const addValues = (r) => addValuesToRequest(user, r)
          return {
            quote: user.quote
              ? [...acc.quote, ...user.quote.map(addValues)]
              : acc.quote,
            supervision: user.supervision
              ? [...acc.supervision, ...user.supervision.map(addValues)]
              : acc.supervision,
          }
        },
        { quote: [], supervision: [] }
      )
      dispatch({ type: 'SET', users, quote, supervision })
    }
  }, [users])

  useEffect(() => {
    if (error && error === 'jwt expired') {
      window.localStorage.removeItem('pxmusic_admin')
    }
  }, [error])

  const hasQuotes = state.quote.length > 0
  const hasSupervision = state.supervision.length > 0

  return (
    <div className="home-grid">
      <div className="home-users">
        <h1 className="home-title" style={{ marginBottom: '1.3em' }}>
          Users
        </h1>
        {state.users.length > 0 ? (
          <ul className="list">
            {users.map(({ name, _id, email }) => (
              <li key={_id} className="list-item item-flex">
                <Link to={`/user/${_id}`}>{name}</Link>
                {profile && profile.superAdmin ? (
                  <span className="list-item-details">
                    <strong>Email</strong>: {email}
                  </span>
                ) : (
                  ''
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="list">No Users yet</div>
        )}
      </div>
      <div className="home-quote">
        <h1 className="home-title">
          Quote{' '}
          {hasQuotes && (
            <button className="order-btn" onClick={changeQuotesOrder}>
              {quotesOrder}
            </button>
          )}
        </h1>
        {hasQuotes ? (
          <ul className="list">
            {state.quote
              .sort(quotesOrder === '↓' ? sortByDate_DESC : sortByDate_ASC)
              .map((info) => (
                <RequestItem {...info} requestType="quote" key={info.id} />
              ))}
          </ul>
        ) : (
          <div className="list">No Quotes yet</div>
        )}
      </div>
      <div className="home-supervision">
        <h1 className="home-title">
          Supervision{' '}
          {hasSupervision && (
            <button className="order-btn" onClick={changeSupervisionOrder}>
              {supervisionOrder}
            </button>
          )}
        </h1>
        {hasSupervision ? (
          <ul className="list">
            {state.supervision
              .sort(supervisionOrder === '↓' ? sortByDate_DESC : sortByDate_ASC)
              .map((info) => (
                <RequestItem
                  {...info}
                  requestType="supervision"
                  key={info.id}
                />
              ))}
          </ul>
        ) : (
          <div className="list">No Supervision yet</div>
        )}
      </div>
    </div>
  )
}

export function sortByDate_DESC(a, b) {
  return new Date(b.date) - new Date(a.date)
}

export function sortByTime_DESC(a, b) {
  return new Date(b.time) - new Date(a.time)
}

export function sortByDate_ASC(a, b) {
  return new Date(a.date) - new Date(b.date)
}

export function RequestItem({
  userId,
  userName,
  id,
  date,
  licenseUser,
  media,
  done,
  requestType,
  label,
  tracks,
}) {
  const { tracksWithoutPrice, tracksPaid, totalTracks } = getTracksStatus(
    tracks
  )
  return (
    <li className={`list-item item-flex ${done ? 'item-alt' : ''}`}>
      <Link to={`/user/${userId}/${requestType}/${id}`}>{id}</Link>
      <span className="list-item-details">
        {label ? (
          label
        ) : (
          <>
            <strong>Date</strong>: {date}.<br />
            {licenseUser ? (
              <>
                <strong>License user</strong>: {licenseUser}. <br />
              </>
            ) : (
              ''
            )}
            {media ? (
              <>
                <strong>Media</strong>: {media}. <br />
              </>
            ) : (
              ''
            )}
            {userName ? (
              <>
                <strong>User</strong>:{' '}
                <Link to={`/user/${userId}`}>{userName}</Link> <br />
              </>
            ) : null}
            {tracksWithoutPrice !== 0 && (
              <>
                <strong>
                  Need to update prices ({tracksWithoutPrice}/{totalTracks})
                </strong>
                <br />
              </>
            )}
            {tracksPaid && tracksPaid.length > 0 && (
              <>
                <strong>
                  Tracks already paid ({tracksPaid.length}/{totalTracks}):{' '}
                </strong>
                {tracksPaid.map((track) => (
                  <span key={track}>{track}</span>
                ))}
              </>
            )}
          </>
        )}
      </span>
    </li>
  )
}

function getTracksStatus(tracks) {
  const tracksWithoutPrice = tracks
    ? tracks.reduce((acc, { price }) => {
        return acc + (!price ? 1 : 0)
      }, 0)
    : 0
  const totalTracks = tracks ? tracks.length : 0
  const tracksPaid = tracks
    ? tracks.reduce((acc, { purchased, name, artist }) => {
        return [...acc, ...(purchased ? [`${name} by ${artist}`] : [])]
      }, [])
    : []

  return { tracksWithoutPrice, totalTracks, tracksPaid }
}
